/**
 * AppBar Help control (DNKADM-0376). Replaces the dead question-mark button
 * with a menu that shows the compiled build stamp (`getBuildId()`) and Copy.
 * Development builds (no compiled id) still open the menu so the control is
 * not dead. Must stay a descendant of `ToastProvider` — do not nest another.
 *
 * DNKADM-0382 — the menu also says whether this build is current, on demand:
 *  - U1: a readable build date, in words, ABOVE the identifier — read from the stamp's
 *    own `YYYYMMDD` prefix (`parseDeployStampDate`), never a second source. A `local-`
 *    build or an unidentifiable one shows no date row. The identifier row and the Copy
 *    row are unchanged; Copy still copies the identifier.
 *  - U2: opening the menu runs the shared build check (`checkBuildCurrency`) and reports
 *    one of three states — checking, up to date, or a new version is available with a
 *    Reload action (the same `reloadForNewBuild` the snackbar uses). Any failure shows
 *    no currency row at all; the stamp and Copy still render. No check fires on mount,
 *    on a timer, or for an unidentifiable build.
 *  - U3: one fetch, one comparison, shared with `StaleBuildNotice`. Opening the menu
 *    does not touch the snackbar's dismiss hold, and a dismissed snackbar does not
 *    suppress the menu's answer.
 */
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getBuildId } from '@/config/buildId';
import { intlLocale } from '@/i18n/constants';
import { parseDeployStampDate } from '@/config/deployStamp';
import { useLocale } from '@/i18n/useLocale';
import { Icon } from '@/theme';
import { checkBuildCurrency, reloadForNewBuild, type BuildCurrency } from './buildCheck';
import { useToast } from './useToast';

/** The menu's currency row: `null` renders no row (never checked, or the check failed). */
type CurrencyState = 'checking' | BuildCurrency | null;

/** The build date in words, in the operator's catalog locale (e.g. "September 7, 2026"). */
function formatBuildDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: 'long' }).format(date);
}

export function HelpMenu() {
  const { t } = useTranslation();
  const locale = useLocale();
  const showToast = useToast();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [currency, setCurrency] = useState<CurrencyState>(null);
  // Each open starts a new check; a result from an earlier open never lands on a later one.
  const checkSeqRef = useRef(0);
  const buildId = getBuildId();
  const buildDate = buildId === null ? null : parseDeployStampDate(buildId);
  const open = Boolean(anchor);

  function handleOpen(event: MouseEvent<HTMLElement>) {
    setAnchor(event.currentTarget);
    if (buildId === null) {
      // Unidentifiable build: no check, no currency row.
      setCurrency(null);
      return;
    }
    const seq = checkSeqRef.current + 1;
    checkSeqRef.current = seq;
    setCurrency('checking');
    void checkBuildCurrency().then((result) => {
      if (checkSeqRef.current !== seq) return;
      setCurrency(result);
    });
  }

  function handleClose() {
    setAnchor(null);
  }

  async function handleCopy() {
    const stamp = getBuildId();
    if (stamp === null) return;
    const clipboard = navigator.clipboard;
    if (clipboard === undefined || typeof clipboard.writeText !== 'function') {
      showToast(t('build.copyError'), 'error');
      return;
    }
    try {
      await clipboard.writeText(stamp);
    } catch {
      showToast(t('build.copyError'), 'error');
      return;
    }
    handleClose();
    showToast(t('build.copied'));
  }

  return (
    <>
      <Tooltip title={t('build.help')}>
        <IconButton
          aria-label={t('build.help')}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={handleOpen}
          sx={{ width: 44, height: 44 }}
        >
          <Icon name="help" size="lg" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={open} onClose={handleClose}>
        {buildId !== null ? (
          <>
            {buildDate !== null && (
              <MenuItem disabled>
                <ListItemText
                  primary={t('build.built', { date: formatBuildDate(buildDate, locale) })}
                  secondary={t('build.buildDate')}
                />
              </MenuItem>
            )}
            <MenuItem disabled>
              <ListItemText
                primary={buildId}
                secondary={t('build.build')}
                slotProps={{ primary: { variant: 'mono' } }}
              />
            </MenuItem>
            <MenuItem onClick={() => void handleCopy()}>
              <ListItemIcon>
                <Icon name="content_copy" size="md" />
              </ListItemIcon>
              {t('build.copyStamp')}
            </MenuItem>
            {currency === 'checking' && (
              <MenuItem disabled>
                <ListItemIcon>
                  <Icon name="sync" size="md" />
                </ListItemIcon>
                <ListItemText primary={t('build.checking')} />
              </MenuItem>
            )}
            {currency === 'current' && (
              <MenuItem disabled>
                <ListItemIcon>
                  <Icon name="check_circle" size="md" />
                </ListItemIcon>
                <ListItemText primary={t('build.upToDate')} secondary={t('build.upToDateDetail')} />
              </MenuItem>
            )}
            {currency === 'stale' && (
              <MenuItem onClick={reloadForNewBuild}>
                <ListItemIcon>
                  <Icon name="refresh" size="md" />
                </ListItemIcon>
                <ListItemText
                  primary={t('build.newVersion')}
                  secondary={t('build.newVersionDetail')}
                />
              </MenuItem>
            )}
          </>
        ) : (
          <MenuItem disabled>
            <ListItemText primary={t('build.development')} />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
